import {MarkType, NodeType} from "../utils";
import {PlateElement, PlateLeaf} from "@udecode/plate/react";
import {SlateElement, SlateLeaf} from "@udecode/plate";
import {MediaPlaceholder, PlateImageElement} from "../elements/MediaPlaceholder";
import {ColumnGroup, ColumnGroupStatic, ColumnItem, ColumnItemStatic} from "../elements/Columns";
import {Paragraph} from "../elements/Paragraph";
import {ReactNode} from "react";
import {attributesToJsx} from "./base-plate-plugins";
import escapeHtml from "escape-html";

const supportedNodeTypes = [
    MarkType.STRONG,
    MarkType.EMPHASIS,
    MarkType.UNDERLINE,
    MarkType.STRIKETHROUGH,
    MarkType.CODE,
    MarkType.HIGHLIGHT,
    NodeType.BLOCK_QUOTE,
    NodeType.HEADING_ONE,
    NodeType.HEADING_TWO,
    NodeType.HEADING_THREE,
    NodeType.HEADING_FOUR,
    NodeType.HEADING_FIVE,
    NodeType.HEADING_SIX,
    NodeType.PARAGRAPH,
    NodeType.UNORDERED_LIST,
    NodeType.ORDERED_LIST,
    NodeType.LIST_ITEM,
    NodeType.LINK,
    NodeType.THEMATIC_BREAK,
    NodeType.IMAGE,
    NodeType.MEDIA_PLACEHOLDER,
    NodeType.COLUMN_GROUP,
    NodeType.COLUMN_ITEM,
    NodeType.IFRAME,
    NodeType.DIV,
] as const;
type ISupportedNodeTypes = typeof supportedNodeTypes[number];

export const basePlateComponentOverrides: {[key in ISupportedNodeTypes]: (props) => ReactNode} = {
    [MarkType.STRONG]: (props) => <PlateLeaf as={"strong"} {...props} />,
    [MarkType.EMPHASIS]: (props) => <PlateLeaf as={"em"} {...props} />,
    [MarkType.UNDERLINE]: (props) => <PlateLeaf as={"u"} {...props} />,
    [MarkType.STRIKETHROUGH]: (props) => <PlateLeaf as={"s"} {...props} />,
    [MarkType.CODE]: (props) => <PlateLeaf as={"code"} {...props} />,
    [MarkType.HIGHLIGHT]: (props) => <PlateLeaf as={"mark"} {...props} />,
    [NodeType.BLOCK_QUOTE]: (props) => <PlateElement as={"blockquote"} {...props} />,
    [NodeType.HEADING_ONE]: (props) => <PlateElement as={"h1"} {...props} />,
    [NodeType.HEADING_TWO]: (props) => <PlateElement as={"h2"} {...props} />,
    [NodeType.HEADING_THREE]: (props) => <PlateElement as={"h3"} {...props} />,
    [NodeType.HEADING_FOUR]: (props) => <PlateElement as={"h4"} {...props} />,
    [NodeType.HEADING_FIVE]: (props) => <PlateElement as={"h5"} {...props} />,
    [NodeType.HEADING_SIX]: (props) => <PlateElement as={"h6"} {...props} />,
    [NodeType.PARAGRAPH]: (props) => <Paragraph {...props} />,
    [NodeType.UNORDERED_LIST]: (props) => <PlateElement as={"ul"} {...props} />,
    [NodeType.ORDERED_LIST]: (props) => <PlateElement as={"ol"} {...props} />,
    [NodeType.LIST_ITEM]: (props) => <PlateElement as={"li"} {...props} />,
    [NodeType.LINK]: (props) => <PlateElement as={"a"} {...props} />,
    [NodeType.THEMATIC_BREAK]: (props) => <hr contentEditable={false}/>,
    [NodeType.IMAGE]: (props) => {
        const { url, alt } = props.element;
        return <PlateImageElement src={url} alt={alt} />
    },
    [NodeType.MEDIA_PLACEHOLDER]: (props) => <MediaPlaceholder {...props} />,
    [NodeType.COLUMN_GROUP]: (props) => <ColumnGroup {...props} />,
    [NodeType.COLUMN_ITEM]: (props) => <ColumnItem {...props} />,
    [NodeType.IFRAME]: (props) => {
        return <PlateElement {...props}>
            <iframe {...props.element.attributes} />
        </PlateElement>
    },
    [NodeType.DIV]: (props) => <PlateElement as={"div"} {...props} />,
}

export const staticPlateComponents: {[key in ISupportedNodeTypes]: (props) => ReactNode} = {
    [MarkType.STRONG]: (props) => <SlateLeaf as={"strong"} {...props} />,
    [MarkType.EMPHASIS]: (props) => <SlateLeaf as={"em"} {...props} />,
    [MarkType.UNDERLINE]: (props) => <SlateLeaf as={"u"} {...props} />,
    [MarkType.STRIKETHROUGH]: (props) => <SlateLeaf as={"s"} {...props} />,
    [MarkType.CODE]: (props) => {
        return <SlateLeaf as={"code"} {...props}>
            {escapeHtml(props.text.text)}
        </SlateLeaf>
    },
    [MarkType.HIGHLIGHT]: (props) => <SlateLeaf as={"mark"} {...props} />,
    [NodeType.BLOCK_QUOTE]: (props) => <SlateElement as={"blockquote"} {...attributesToJsx(props.element.attributes)} {...props} />,
    [NodeType.HEADING_ONE]: (props) => <SlateElement as={"h1"} {...attributesToJsx(props.element.attributes)} {...props} />,
    [NodeType.HEADING_TWO]: (props) => <SlateElement as={"h2"} {...attributesToJsx(props.element.attributes)} {...props} />,
    [NodeType.HEADING_THREE]: (props) => <SlateElement as={"h3"} {...attributesToJsx(props.element.attributes)} {...props} />,
    [NodeType.HEADING_FOUR]: (props) => <SlateElement as={"h4"} {...attributesToJsx(props.element.attributes)} {...props} />,
    [NodeType.HEADING_FIVE]: (props) => <SlateElement as={"h5"} {...attributesToJsx(props.element.attributes)} {...props} />,
    [NodeType.HEADING_SIX]: (props) => <SlateElement as={"h6"} {...attributesToJsx(props.element.attributes)} {...props} />,
    [NodeType.PARAGRAPH]: (props) => <SlateElement as={"p"} {...attributesToJsx(props.element.attributes)} {...props} />,
    [NodeType.UNORDERED_LIST]: (props) => <SlateElement as={"ul"} {...attributesToJsx(props.element.attributes)} {...props} />,
    [NodeType.ORDERED_LIST]: (props) => <SlateElement as={"ol"} {...attributesToJsx(props.element.attributes)} {...props} />,
    [NodeType.LIST_ITEM]: (props) => <SlateElement as={"li"} {...attributesToJsx(props.element.attributes)} {...props} />,
    [NodeType.LINK]: (props) => <SlateElement as={"a"} {...attributesToJsx(props.element.attributes)} {...props} />,
    [NodeType.THEMATIC_BREAK]: (props) => <hr contentEditable={false}/>,
    [NodeType.IMAGE]: (props) => <img src={props.element.url} alt={props.element.alt} {...attributesToJsx(props.element.attributes)} />,
    [NodeType.COLUMN_GROUP]: (props) => <ColumnGroupStatic {...props} />,
    [NodeType.COLUMN_ITEM]: (props) => <ColumnItemStatic {...props} />,
    [NodeType.MEDIA_PLACEHOLDER]: (props) => {
        switch (props.element.mediaType) {
            case "img":
                return <img data-id={props.element.id} data-media-type={props.element.mediaType} data-type={props.element.type} />
            default:
                return <></>
        }
    },
    [NodeType.IFRAME]: (props) => {
        return <iframe {...attributesToJsx(props.element.attributes)} />
    },
    [NodeType.DIV]: (props) => <SlateElement as={"div"} {...attributesToJsx(props.element.attributes)} {...props} />,
}